import { AxiosError } from 'axios';

type LocalizeText = (textId: string, defaultText: string) => string;

/**
 * The subscription and pass errors the app can react to, spelled as the
 * backend's `string_code`.
 *
 * Branch on this, never on the numeric `code`: these errors are declared with a
 * status and message only, so they all share `code: -1` with `UNEXPECTED_ERROR`
 * and are indistinguishable by number.
 */
export enum SubscriptionErrorCode {
  PRICING_NOT_SUPPORTED_AT_STATION = 'PRICING_NOT_SUPPORTED_AT_STATION',
  STATION_PRICING_NOT_FOUND = 'STATION_PRICING_NOT_FOUND',
  SUBSCRIPTION_ALREADY_EXISTED = 'SUBSCRIPTION_ALREADY_EXISTED',
  SUBSCRIPTION_REACH_MAX_SWAP = 'SUBSCRIPTION_REACH_MAX_SWAP',
  SUBSCRIPTION_INVALID_STATE = 'SUBSCRIPTION_INVALID_STATE',
  SUBSCRIPTION_NOT_FOUND = 'SUBSCRIPTION_NOT_FOUND',
  USER_PASS_NOT_FOUND = 'USER_PASS_NOT_FOUND',
  USER_PASS_INVALID_STATE = 'USER_PASS_INVALID_STATE',
  PHONE_NUMBER_IS_REQUIRED = 'PHONE_NUMBER_IS_REQUIRED',
}

const FALLBACK_MESSAGES: Record<SubscriptionErrorCode, string> = {
  [SubscriptionErrorCode.PRICING_NOT_SUPPORTED_AT_STATION]:
    'This option isn’t available at this station. Please choose another one.',
  [SubscriptionErrorCode.STATION_PRICING_NOT_FOUND]:
    'This option isn’t available at this station. Please choose another one.',
  [SubscriptionErrorCode.SUBSCRIPTION_ALREADY_EXISTED]:
    'You already have a pass.',
  [SubscriptionErrorCode.SUBSCRIPTION_REACH_MAX_SWAP]:
    'You’ve used all the swaps in your current cycle.',
  [SubscriptionErrorCode.SUBSCRIPTION_INVALID_STATE]:
    'Your pass changed since this screen loaded. Please try again.',
  [SubscriptionErrorCode.SUBSCRIPTION_NOT_FOUND]:
    'We couldn’t find that subscription. Please refresh and try again.',
  [SubscriptionErrorCode.USER_PASS_NOT_FOUND]:
    'We couldn’t find your pass. Please refresh and try again.',
  // Covers a cancel refused while a renewal is in flight as well as a genuinely
  // stale screen, so it must not promise that retrying will work.
  [SubscriptionErrorCode.USER_PASS_INVALID_STATE]:
    'Your pass can’t be changed right now. Refresh for its latest state.',
  [SubscriptionErrorCode.PHONE_NUMBER_IS_REQUIRED]:
    'Add a phone number to your account before buying a pass.',
};

const isSubscriptionErrorCode = (
  value: unknown,
): value is SubscriptionErrorCode =>
  typeof value === 'string' &&
  Object.values(SubscriptionErrorCode).includes(value as SubscriptionErrorCode);

/**
 * The recognised `string_code` on an error, or `null` when it is some other
 * failure the caller should keep handling itself.
 */
export const getSubscriptionErrorCode = (
  error: unknown,
): SubscriptionErrorCode | null => {
  if (!(error instanceof AxiosError)) {
    return null;
  }

  const stringCode = (
    error.response?.data as { stringCode?: string } | undefined
  )?.stringCode;

  return isSubscriptionErrorCode(stringCode) ? stringCode : null;
};

/**
 * A localized message for a recognised subscription error, or `null` to let the
 * caller fall back to its own handling.
 */
export const getSubscriptionErrorMessage = (
  error: unknown,
  lt: LocalizeText,
): string | null => {
  const code = getSubscriptionErrorCode(error);

  if (!code) {
    return null;
  }

  return lt(`text.subscriptionError.${code}`, FALLBACK_MESSAGES[code]);
};
